/**
 * API client. Plan §2.4: every mutation carries an `Idempotency-Key` and the
 * CSRF token, and errors come back as `{ code, message, field?, details? }`.
 */
import { uuidv7 } from 'uuidv7'
import { ApiError, type ApiErrorBody, type ErrorCode } from '@rowted/shared'

let csrfToken: string | null = null

export const setCsrfToken = (token: string | null): void => {
  csrfToken = token
}

/**
 * The token, for a request that cannot go through `request` or `upload` — an
 * upload that reports its progress (`XMLHttpRequest`), from the scope
 * screens' queue (`docs/activity.md` §11.4).
 */
export const currentCsrfToken = (): string | null => csrfToken

/*
 * The chosen branches, on every request.
 *
 * Plan §2.4: the scope selector is per device, so the client remembers it and
 * sends it as `?scope=`; the server validates it and narrows every query to
 * those org units. Nothing was sending it, so `selectedScope` on the server
 * fell through to "everything you can reach" and the picker in the header
 * changed the label and nothing else.
 *
 * Here rather than threaded through every caller for the same reason the CSRF
 * token is here: it belongs to the connection, not to the call, and a hundred
 * call sites each remembering to pass it is a hundred chances for one to
 * forget — which is exactly the bug this is.
 */
const SCOPE_KEY = 'rowted.scope'

let scopeIds: string[] = readScope()

function readScope(): string[] {
  try {
    const stored = window.localStorage.getItem(SCOPE_KEY)
    const parsed: unknown = stored ? JSON.parse(stored) : null
    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : []
  } catch {
    // Private windows, blocked site data, and anywhere `localStorage` throws
    // rather than returning null. No remembered scope is a working app.
    return []
  }
}

/** What the header's picker chose, remembered for this device. */
export function setScope(orgUnitIds: string[]): void {
  scopeIds = orgUnitIds
  try {
    window.localStorage.setItem(SCOPE_KEY, JSON.stringify(orgUnitIds))
  } catch { /* see readScope */ }
}

export const currentScope = (): string[] => scopeIds

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'
  body?: unknown
  /** Pass the key through on a retry so the server replays rather than repeats. */
  idempotencyKey?: string
  scope?: string[]
  signal?: AbortSignal
  /** Extra request headers — Activity's device key (`docs/activity.md` §4.6). */
  headers?: Record<string, string>
  /** Let it finish after the page goes away — a save sent as the page hands itself to another app. */
  keepalive?: boolean
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET'
  const url = new URL(`/api/v1${path}`, window.location.origin)
  // The call may name its own scope — the scope picker's own validation POST
  // must not be narrowed by the scope it is about to replace.
  const scope = options.scope ?? scopeIds
  if (scope.length) url.searchParams.set('scope', scope.join(','))

  const headers: Record<string, string> = { Accept: 'application/json', ...options.headers }
  if (options.body !== undefined) headers['Content-Type'] = 'application/json'

  if (method !== 'GET') {
    // Generated at the moment the user acts, so a retry of the *same* action
    // reuses it and a new action does not (addendum §H.1).
    headers['Idempotency-Key'] = options.idempotencyKey ?? uuidv7()
    if (csrfToken) headers['x-rowted-csrf'] = csrfToken
  }

  const response = await fetch(url, {
    method,
    headers,
    credentials: 'same-origin',
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
    signal: options.signal,
    ...(options.keepalive ? { keepalive: true } : {}),
  })

  if (response.status === 204) return undefined as T

  const payload = await response.json().catch(() => null)

  if (!response.ok) {
    const body = payload as ApiErrorBody | null
    throw new ApiError(
      (body?.code ?? 'internal_error') as ErrorCode,
      body?.message ?? 'Something went wrong.',
      { field: body?.field, details: body?.details, status: response.status },
    )
  }

  return payload as T
}

export interface UploadedFile {
  id: string
  name: string
  contentType: string
  byteSize: number
  url: string
}

/**
 * Sending a file.
 *
 * The body is the file and nothing else — no `FormData`, no boundary, no
 * multipart parser on the other end. The name and the kind go in the query
 * string where the server can validate them like any other input, and the
 * browser sets `Content-Type` from the `File` itself, which is the one place
 * that value is actually known.
 */
export async function upload(
  file: File,
  options: { kind?: string; orgUnitId?: string } = {},
): Promise<UploadedFile> {
  const url = new URL('/api/v1/files', window.location.origin)
  url.searchParams.set('name', file.name)
  if (options.kind) url.searchParams.set('kind', options.kind)
  if (options.orgUnitId) url.searchParams.set('orgUnitId', options.orgUnitId)

  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Content-Type': file.type || 'application/octet-stream',
    // An upload is a mutation like any other (§H.1). Without this the request
    // is refused, which is the right answer — a rule with an exception in it
    // is a rule somebody has to remember.
    'Idempotency-Key': uuidv7(),
  }
  if (csrfToken) headers['x-rowted-csrf'] = csrfToken

  const response = await fetch(url, {
    method: 'POST',
    headers,
    credentials: 'same-origin',
    body: file,
  })

  const payload = await response.json().catch(() => null)
  if (!response.ok) {
    const body = payload as ApiErrorBody | null
    throw new ApiError(
      (body?.code ?? 'internal_error') as ErrorCode,
      body?.message ?? 'That file could not be uploaded.',
      { field: body?.field, details: body?.details, status: response.status },
    )
  }
  return payload as UploadedFile
}

/**
 * Sending a logo into one of the company's three slots.
 *
 * The same raw-bytes shape as `upload`, pointed at `/company/logo`, because
 * a logo is not attached to a record: it *is* the slot, and the server sets
 * the profile's column in the same transaction that stores the file. Doing it
 * with `upload` and a second call would leave a window where the file exists
 * and no slot names it.
 */
export async function uploadLogo(
  slot: 'light' | 'dark' | 'mark', file: File,
): Promise<{ slot: string; fileId: string; url: string }> {
  const url = new URL('/api/v1/company/logo', window.location.origin)
  url.searchParams.set('slot', slot)
  url.searchParams.set('name', file.name)

  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Content-Type': file.type || 'application/octet-stream',
    'Idempotency-Key': uuidv7(),
  }
  if (csrfToken) headers['x-rowted-csrf'] = csrfToken

  const response = await fetch(url, {
    method: 'POST', headers, credentials: 'same-origin', body: file,
  })
  const payload = await response.json().catch(() => null)
  if (!response.ok) {
    const body = payload as ApiErrorBody | null
    throw new ApiError(
      (body?.code ?? 'internal_error') as ErrorCode,
      body?.message ?? 'That logo could not be uploaded.',
      { field: body?.field, details: body?.details, status: response.status },
    )
  }
  return payload as { slot: string; fileId: string; url: string }
}

/**
 * An image for an email signature (Aaron, Oct 24, note 2.7): the bytes, as a
 * logo goes, filed where the public image route will serve it. `src` is the
 * path a signature names it by.
 */
export async function uploadSignatureImage(file: File): Promise<{ id: string; src: string }> {
  const url = new URL('/api/v1/settings/email-signatures/images', window.location.origin)
  url.searchParams.set('name', file.name)
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Content-Type': file.type || 'application/octet-stream',
    'Idempotency-Key': uuidv7(),
  }
  if (csrfToken) headers['x-rowted-csrf'] = csrfToken
  const response = await fetch(url, { method: 'POST', headers, credentials: 'same-origin', body: file })
  const payload = await response.json().catch(() => null)
  if (!response.ok) {
    const body = payload as ApiErrorBody | null
    throw new ApiError(
      (body?.code ?? 'internal_error') as ErrorCode,
      body?.message ?? 'That image could not be uploaded.',
      { field: body?.field, details: body?.details, status: response.status },
    )
  }
  return payload as { id: string; src: string }
}
